import { ChangeEventHandler, FC } from 'react';

import { useAtomValue } from 'jotai';
import useSWR from 'swr';

import { isUserHeadSupportAtom, isUserSupportAtom } from '../../atoms/userAtom';

import { PayOutDiamondsButton } from './payOutDiamondsButton/PayOutDiamondsButton';
import { UserSettingsTable } from './userSettingsTable/UserSettingsTable';

import css from './UserSettingsPage.module.css';

import { userService } from 'v2/api/userService';
import { PageLayout } from 'v2/components/pageLayout/PageLayout';
import { PageTitle } from 'v2/components/pageLayout/pageTitle/PageTitle';
import { SearchTextField } from 'v2/components/searchTextField/SearchTextField';
import { Select } from 'v2/components/select/Select';
import { Geo, geoToUIMap } from 'v2/constants';
import { useFilter } from 'v2/hooks/useFilter';
import { usePagination } from 'v2/hooks/usePagination';
import { SelectOption } from 'v2/types';
import { UserFilter } from 'v2/types/UserModel';

const GEO_OPTIONS: SelectOption<Geo | ''>[] = [
  { id: 'all', label: 'All', value: '' },
  ...Object.values(Geo).map((g) => ({ id: g, label: geoToUIMap[g].label, value: g })),
];

export const UserSettingsPage: FC = () => {
  const pagination = usePagination();
  const isUserSupport = useAtomValue(isUserSupportAtom);
  const isUserHeadSupport = useAtomValue(isUserHeadSupportAtom);

  const { filter, debouncedFilter, onChange } = useFilter<UserFilter>({
    userNickname: '',
    telegramUserName: '',
    email: '',
    geo: '',
  });

  const { data, mutate } = useSWR(
    ['getUsers', pagination.page, pagination.pageSize, debouncedFilter],
    userService.getUsers,
    {
      keepPreviousData: true,
    }
  );

  const handleFilterChange =
    (field: keyof UserFilter): ChangeEventHandler<HTMLInputElement> =>
    (e) => {
      onChange(field, e.target.value);
    };

  return (
    <PageLayout className={css.userSettingsPage} overflow="hidden">
      <PageTitle action={!isUserSupport && !isUserHeadSupport && <PayOutDiamondsButton onSuccess={mutate} />}>
        User Settings
      </PageTitle>

      <div className="form-group">
        <SearchTextField
          value={filter.userNickname}
          onChange={handleFilterChange('userNickname')}
          label="User Nickname"
        />

        <SearchTextField
          value={filter.telegramUserName}
          onChange={handleFilterChange('telegramUserName')}
          label="Telegram Username"
        />

        <SearchTextField value={filter.email} onChange={handleFilterChange('email')} label="Email" />

        <div style={{ width: 160, flexShrink: 0 }}>
          <Select<Geo | ''>
            value={filter.geo}
            options={GEO_OPTIONS}
            label="Geo interface"
            size="small"
            onChange={(value) => onChange('geo', value)}
          />
        </div>
      </div>

      <UserSettingsTable
        data={data}
        pagination={pagination}
        onRevalidate={mutate}
        isUserSupport={isUserSupport}
        isUserHeadSupport={isUserHeadSupport}
      />
    </PageLayout>
  );
};
